import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV, CHURCH } from "@/lib/data";
import ThemeToggle from "@/components/ui/ThemeToggle";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  // Links absolutos (começam com /) vão direto
  // Links de âncora (#) na homepage vão direto, em outras rotas voltam para home
  const buildHref = (href) => {
    if (href.startsWith("/")) return href; // Links absolutos
    return onHome ? href : `/${href}`; // Âncoras
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <nav className="flex items-center justify-between h-20">
          <Link
            to={buildHref("#inicio")}
            data-testid="nav-logo"
            className="flex items-center gap-4 group"
          >
            <span className="relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-white/95 rounded-full overflow-hidden shrink-0 transition-transform duration-500 group-hover:scale-105">
              <img
                src="/logo-pibg.png"
                alt="Logo PIBG"
                className="w-full h-full object-contain p-1"
              />
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif-display text-2xl lg:text-3xl tracking-tight text-white">
                PIB<span className="text-[#D4AF37]">G</span>
              </span>
              <span className="text-[10px] lg:text-xs uppercase tracking-widest text-white/55">
                Greenville · SC
              </span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-10">
            {NAV.map((item) => (
              <li key={item.href}>
                <Link
                  to={buildHref(item.href)}
                  data-testid={`nav-link-${item.href.replace("#", "")}`}
                  className="text-sm tracking-wide text-white/75 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => {
                const givingSection = document.getElementById('giving-section');
                if (givingSection) {
                  givingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              data-testid="nav-donate"
              className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-overline bg-[#D4AF37] text-black hover:bg-[#B3932E] transition-all duration-300"
            >
              <Heart size={16} />
              Doar
            </button>
            <Link
              to={buildHref("#contato")}
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-overline border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
            >
              Visite-nos
            </Link>
          </div>

          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-white"
            aria-label="Abrir menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>

          <ThemeToggle />
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/5"
            data-testid="nav-mobile-panel"
          >
            <ul className="px-6 py-8 space-y-6">
              {NAV.map((item) => (
                <li key={item.href}>
                  <Link
                    to={buildHref(item.href)}
                    onClick={close}
                    className="block font-serif-display text-3xl text-white hover:text-[#D4AF37] transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={buildHref("#contato")}
                  onClick={close}
                  className="inline-block mt-4 px-6 py-3 text-xs uppercase tracking-overline border border-[#D4AF37]/40 text-[#D4AF37]"
                >
                  Visite-nos
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
